import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Button,
  TextField,
  Alert,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from "@mui/material";
import {
  Email as EmailIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../store";
import { saveNotificationPreferences } from "../../features/notifications/notificationSlice";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebaseAuth";
import {
  areNotificationsSupported,
  getBrowserLimitations,
} from "../../utils/browserDetection";

interface EmailNotificationBlockProps {
  userId: string;
}

// Fonction de validation d'email simple
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const EmailNotificationBlock: React.FC<EmailNotificationBlockProps> = ({
  userId,
}) => {
  const dispatch = useAppDispatch();
  const { preferences, loading } = useAppSelector(
    (state) => state.notifications
  );
  const [user] = useAuthState(auth);

  // État local pour les paramètres email
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    emails: [] as string[],
  });

  // État pour l'ajout d'email
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [switchLoading, setSwitchLoading] = useState(false);
  const [emailOperationLoading, setEmailOperationLoading] = useState(false);

  const pushSupported = areNotificationsSupported();
  const browserLimitations = getBrowserLimitations();

  // Déterminer l'état par défaut des notifications email
  const getDefaultEmailState = () => {
    // Si les push ne sont pas supportées, activer les emails par défaut
    if (!pushSupported) return true;
    // Si les push sont supportées, désactiver les emails par défaut
    return false;
  };

  // Initialiser les paramètres depuis les préférences
  useEffect(() => {
    if (preferences?.emailNotifications) {
      setEmailSettings({
        enabled: preferences.emailNotifications.enabled || false,
        emails: preferences.emailNotifications.emails || [],
      });
    } else {
      // Initialiser avec l'email de l'utilisateur connecté
      const defaultEmail = user?.email || "";
      setEmailSettings({
        enabled: getDefaultEmailState(),
        emails: defaultEmail ? [defaultEmail] : [],
      });
    }
  }, [preferences, user]);

  const handleEmailToggle = async () => {
    const newEnabled = !emailSettings.enabled;

    // Sauvegarder immédiatement comme pour les notifications push
    try {
      setSwitchLoading(true);
      await dispatch(
        saveNotificationPreferences({
          userId,
          preferences: {
            emailNotifications: {
              ...emailSettings,
              enabled: newEnabled,
            },
          },
        })
      ).unwrap();

      // Mettre à jour l'état local
      setEmailSettings((prev) => ({
        ...prev,
        enabled: newEnabled,
      }));
    } catch (error) {
      console.error(
        "Erreur lors de la sauvegarde des paramètres email:",
        error
      );
    } finally {
      setSwitchLoading(false);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) {
      setEmailError("Veuillez saisir une adresse email");
      return;
    }

    if (!validateEmail(newEmail.trim())) {
      setEmailError("Adresse email invalide");
      return;
    }

    if (emailSettings.emails.includes(newEmail.trim())) {
      setEmailError("Cette adresse email est déjà ajoutée");
      return;
    }

    const updatedEmails = [...emailSettings.emails, newEmail.trim()];

    // Sauvegarder automatiquement
    try {
      setEmailOperationLoading(true);
      await dispatch(
        saveNotificationPreferences({
          userId,
          preferences: {
            emailNotifications: {
              ...emailSettings,
              emails: updatedEmails,
            },
          },
        })
      ).unwrap();

      setEmailSettings((prev) => ({
        ...prev,
        emails: updatedEmails,
      }));
      setNewEmail("");
      setEmailError("");
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'email:", error);
      setEmailError("Erreur lors de l'ajout de l'email");
    } finally {
      setEmailOperationLoading(false);
    }
  };

  const handleRemoveEmail = async (emailToRemove: string) => {
    const updatedEmails = emailSettings.emails.filter(
      (email) => email !== emailToRemove
    );

    // Sauvegarder automatiquement
    try {
      setEmailOperationLoading(true);
      await dispatch(
        saveNotificationPreferences({
          userId,
          preferences: {
            emailNotifications: {
              ...emailSettings,
              emails: updatedEmails,
            },
          },
        })
      ).unwrap();

      setEmailSettings((prev) => ({
        ...prev,
        emails: updatedEmails,
      }));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'email:", error);
    } finally {
      setEmailOperationLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleAddEmail();
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <EmailIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6" component="h3">
          Notifications par Email
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          {emailSettings.enabled && (
            <Chip label="Activé" color="success" size="small" />
          )}
          {emailSettings.emails.length > 0 && (
            <Chip
              label={`${emailSettings.emails.length} email${
                emailSettings.emails.length > 1 ? "s" : ""
              }`}
              color="info"
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* État actuel */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={emailSettings.enabled}
              onChange={handleEmailToggle}
              disabled={switchLoading || loading}
              color="primary"
            />
          }
          label={`Notifications par email ${
            emailSettings.enabled ? "activées" : "désactivées"
          }`}
        />
      </Box>

      {/* Messages d'information selon le contexte */}
      {!pushSupported && emailSettings.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les notifications push ne sont pas supportées sur votre navigateur.
          Les notifications par email sont activées par défaut.
        </Alert>
      )}

      {pushSupported && !emailSettings.enabled && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Les notifications push sont disponibles. Vous pouvez activer les
          notifications par email en complément.
        </Alert>
      )}

      {/* Configuration des adresses email (visible si activé) */}
      {emailSettings.enabled && (
        <>
          <Divider sx={{ my: 2 }} />

          {/* Gestion des adresses email */}
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Adresses email pour les notifications
          </Typography>

          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Ajouter une adresse email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              error={!!emailError}
              helperText={emailError}
              placeholder="exemple@email.com"
              disabled={emailOperationLoading}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={handleAddEmail}
                    color="primary"
                    disabled={emailOperationLoading}
                  >
                    <AddIcon />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {/* Liste des emails */}
          {emailSettings.emails.length > 0 && (
            <List dense>
              {emailSettings.emails.map((email, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemText
                    primary={email}
                    secondary={
                      index === 0 ? "Email principal" : "Email secondaire"
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveEmail(email)}
                      disabled={
                        emailSettings.emails.length === 1 ||
                        emailOperationLoading
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {emailSettings.emails.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucune adresse email configurée. Ajoutez au moins une adresse pour
              recevoir les notifications.
            </Alert>
          )}
        </>
      )}

      {/* Informations générales */}
      <Typography variant="body2" color="text.secondary">
        Les notifications par email vous informent des changements de statut des
        matchs de vos joueurs suivis directement dans votre boîte mail.
      </Typography>
    </Paper>
  );
};

export default EmailNotificationBlock;
